import React, { useRef, useState } from 'react';
import { Search, Filter, X } from 'lucide-react';

/**
 * AnimatedSearchBar — barra de busca/filtros expansível padronizada.
 * Mesma UX da página de Romaneios.
 *
 * Props:
 * - value, onChange, placeholder  → input de busca
 * - filtersPanel (ReactNode)      → conteúdo do painel de filtros (grid)
 * - activeFiltersCount (number)   → badge no botão Filtros
 * - onClearAll (fn)               → handler para "Limpar filtros"
 * - rightSlot (ReactNode)         → conteúdo adicional à direita (ex: toggle visualização)
 * - testIdPrefix (string)         → prefixo p/ data-testid
 * - hideFilters (bool)            → quando true, esconde botão Filtros (só busca)
 */
export default function AnimatedSearchBar({
  value,
  onChange,
  placeholder = 'Buscar...',
  filtersPanel,
  activeFiltersCount = 0,
  onClearAll,
  rightSlot,
  testIdPrefix = 'asb',
  hideFilters = false,
}) {
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const inputRef = useRef(null);

  const hasSearch = !!value;
  const showClearAll = (hasSearch || activeFiltersCount > 0) && !!onClearAll;

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-3" data-testid={`${testIdPrefix}-container`}>
      <div className="flex flex-wrap items-center gap-3">
        {/* Busca expansível */}
        <div
          className={`relative flex items-center transition-all duration-300 ease-out overflow-hidden ${
            searchExpanded || hasSearch ? 'flex-1 min-w-[240px]' : 'w-11'
          }`}
          data-testid={`${testIdPrefix}-search-wrapper`}
        >
          <button
            type="button"
            onClick={() => {
              setSearchExpanded(true);
              setTimeout(() => inputRef.current?.focus(), 50);
            }}
            className="absolute left-0 top-0 h-11 w-11 flex items-center justify-center text-muted-foreground hover:text-foreground rounded-lg border border-border bg-background cursor-pointer transition-colors z-10"
            aria-label="Abrir busca"
            data-testid={`${testIdPrefix}-search-toggle-btn`}
          >
            <Search className="w-4 h-4" />
          </button>
          <input
            ref={inputRef}
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => setSearchExpanded(true)}
            onBlur={() => { if (!value) setSearchExpanded(false); }}
            className={`h-11 w-full rounded-lg border border-border bg-background pl-12 pr-9 text-sm outline-none transition-opacity duration-200 focus:ring-2 focus:ring-primary/40 ${
              searchExpanded || hasSearch ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            data-testid={`${testIdPrefix}-search-input`}
          />
          {hasSearch && (
            <button
              type="button"
              onClick={() => { onChange(''); inputRef.current?.focus(); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full text-muted-foreground hover:bg-muted z-10"
              aria-label="Limpar busca"
              data-testid={`${testIdPrefix}-search-clear-btn`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Botão Filtros */}
        {!hideFilters && filtersPanel && (
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`btn-secondary flex items-center gap-2 ${showFilters ? 'bg-primary/10 border-primary' : ''}`}
            data-testid={`${testIdPrefix}-filter-toggle-btn`}
          >
            <Filter className="w-4 h-4" />
            Filtros
            {activeFiltersCount > 0 && (
              <span className="bg-primary text-white text-xs px-1.5 py-0.5 rounded-full">
                {activeFiltersCount}
              </span>
            )}
          </button>
        )}

        {/* Limpar Filtros */}
        {showClearAll && (
          <button
            type="button"
            onClick={() => { onClearAll(); setShowFilters(false); }}
            className="text-sm text-muted-foreground hover:text-foreground"
            data-testid={`${testIdPrefix}-clear-all-btn`}
          >
            Limpar filtros
          </button>
        )}

        {/* Slot à direita (toggle visualização etc) */}
        {rightSlot && <div className="ml-auto flex items-center gap-2">{rightSlot}</div>}
      </div>

      {/* Painel de Filtros */}
      {!hideFilters && showFilters && filtersPanel && (
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-3 border-t border-border animate-in slide-in-from-top-2"
          data-testid={`${testIdPrefix}-filters-panel`}
        >
          {filtersPanel}
        </div>
      )}
    </div>
  );
}
